
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Loader2, Save, Send, Trash2, UserRound, FileText, Plus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { toast } from 'sonner';
import CaseInfoSidePanel from '@/components/treatmentPlans/CaseInfoSidePanel';
import { useCreateTreatmentPlan } from '@/hooks/useCreateTreatmentPlan';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface CreateTreatmentPlanProps {
  patientId: string;
}

interface PatientInfo {
  id: string;
  first_name: string;
  last_name: string;
  avatar_url: string | null;
}

interface ExistingTreatmentPlan {
  id: string;
  title: string;
  status: string;
  created_at: string;
}

const CreateTreatmentPlan: React.FC<CreateTreatmentPlanProps> = ({ patientId }) => {
  const navigate = useNavigate();
  const [patient, setPatient] = useState<PatientInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [existingTreatmentPlans, setExistingTreatmentPlans] = useState<ExistingTreatmentPlan[]>([]);
  const [isDraftMode, setIsDraftMode] = useState(false);
  const { 
    title, 
    setTitle, 
    description, 
    setDescription, 
    treatments, 
    addTreatment, 
    removeTreatment, 
    calculateTotal, 
    submitPlan,
    resetForm
  } = useCreateTreatmentPlan();

  useEffect(() => {
    const fetchPatient = async () => {
      try {
        setIsLoading(true);
        const { data, error } = await supabase
          .from('patient_profiles_view')
          .select('id, first_name, last_name, avatar_url')
          .eq('id', patientId)
          .single();

        if (error) throw error;
        setPatient(data);
        
        // Fetch existing treatment plans for this patient
        const { data: planData, error: planError } = await supabase
          .from('treatment_plans')
          .select('id, title, status, created_at')
          .eq('patient_id', patientId)
          .order('created_at', { ascending: false });
          
        if (planError) throw planError;
        setExistingTreatmentPlans(planData || []);
        
      } catch (error: any) {
        console.error('Error fetching patient:', error.message);
        toast.error('Failed to load patient information');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPatient();
  }, [patientId]);

  const handleReset = () => {
    resetForm();
    toast.info('Treatment plan form has been reset');
  };

  const handleSaveDraft = async () => {
    try {
      const planId = await submitPlan('draft', patientId);
      if (planId) {
        toast.success('Treatment plan saved as draft');
        navigate(`/treatment-plans/${patientId}`);
      }
    } catch (error) {
      toast.error('Failed to save draft');
    }
  };

  const handleSubmit = async () => {
    try {
      if (treatments.length === 0) {
        toast.error('Please add at least one treatment before submitting');
        return;
      }
      
      const planId = await submitPlan('sent', patientId);
      if (planId) {
        toast.success('Treatment plan submitted successfully');
        navigate(`/treatment-plans/${patientId}`);
      }
    } catch (error) {
      toast.error('Failed to submit treatment plan');
    }
  };

  const handleViewPlan = (planId: string) => {
    navigate(`/treatment-plan/${planId}`);
  };

  const handleCreateNewPlan = () => {
    setIsDraftMode(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary mr-2" />
        <span>Loading patient information...</span>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="container mx-auto p-4 text-center">
        <h1 className="text-2xl font-bold mb-4">Patient Not Found</h1>
        <p className="mb-4">The requested patient could not be found.</p>
        <Button onClick={() => navigate('/patients')}>Return to Patients</Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Create Treatment Plan</h1>
          <p className="text-muted-foreground">Create a new treatment plan for {patient.first_name} {patient.last_name}</p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={handleReset}>
            <Trash2 className="mr-2 h-4 w-4" />
            Reset
          </Button>
          <Button variant="outline" onClick={handleSaveDraft}>
            <Save className="mr-2 h-4 w-4" />
            Save Draft
          </Button>
          <Button onClick={handleSubmit}>
            <Send className="mr-2 h-4 w-4" />
            Submit
          </Button>
        </div>
      </div>

      <Tabs defaultValue={existingTreatmentPlans.length > 0 && !isDraftMode ? "existing" : "new"}>
        <TabsList className="mb-4">
          {existingTreatmentPlans.length > 0 && (
            <TabsTrigger value="existing">Existing Plans</TabsTrigger>
          )}
          <TabsTrigger value="new">New Plan</TabsTrigger>
        </TabsList>

        {existingTreatmentPlans.length > 0 && (
          <TabsContent value="existing">
            <Card>
              <CardHeader>
                <CardTitle>Existing Treatment Plans</CardTitle>
              </CardHeader>
              <CardContent>
                {existingTreatmentPlans.map((plan) => (
                  <div key={plan.id} className="p-4 border rounded-md mb-3 flex justify-between items-center">
                    <div>
                      <h3 className="font-semibold">{plan.title || "Treatment Plan"}</h3>
                      <p className="text-sm text-muted-foreground">
                        Status: <span className="capitalize">{plan.status.replace('_', ' ')}</span>
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Created: {new Date(plan.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Button variant="outline" onClick={() => handleViewPlan(plan.id)}>
                      <FileText className="h-4 w-4 mr-2" />
                      View Plan
                    </Button>
                  </div>
                ))}
                <div className="mt-4">
                  <Button onClick={handleCreateNewPlan} variant="secondary">
                    <Plus className="h-4 w-4 mr-2" />
                    Create New Plan
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        <TabsContent value="new">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center mb-6">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center mr-3">
                      {patient.avatar_url ? (
                        <img 
                          src={patient.avatar_url} 
                          alt={`${patient.first_name} ${patient.last_name}`} 
                          className="h-10 w-10 rounded-full object-cover" 
                        />
                      ) : (
                        <UserRound className="h-5 w-5 text-primary" />
                      )}
                    </div>
                    <div>
                      <h2 className="font-semibold">{patient.first_name} {patient.last_name}</h2>
                      <p className="text-sm text-muted-foreground">Patient ID: {patientId}</p>
                    </div>
                  </div>

                  <p className="text-center p-8 text-muted-foreground">
                    Tooth chart and treatment plan builder will be displayed here...
                  </p>
                </CardContent>
              </Card>
            </div>
            
            <div className="lg:col-span-1">
              {/* Summary/Controls Panel would go here */}
              <Card>
                <CardContent className="p-6">
                  <h2 className="font-semibold mb-4">Plan Summary</h2>
                  <p className="text-center p-4 text-muted-foreground">
                    Treatment summary will appear here as items are added to the plan.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add the side panel for case information */}
      <CaseInfoSidePanel patientId={patientId} />
    </div>
  );
};

export default CreateTreatmentPlan;
